import Graphic from '@arcgis/core/Graphic';
import Map from '@arcgis/core/Map';
import Point from '@arcgis/core/geometry/Point';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import MapView from '@arcgis/core/views/MapView';
import { Box } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface SimpleArcGISMapProps {
  center?: [number, number];
  zoom?: number;
  height?: string | number;
  width?: string | number;
  onPinLatLong?: (lat: number, lng: number) => void;
}

const SimpleArcGISMap: React.FC<SimpleArcGISMapProps> = ({
  center = [-47.9292, -15.7801],
  zoom = 22,
  height = 800,
  width = '100%',
  onPinLatLong,
}) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const view = useRef<MapView | null>(null);
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(center);

  useEffect(() => {
    if (mapDiv.current) {
      const map = new Map({
        basemap: 'streets-navigation-vector'
      });

      view.current = new MapView({
        container: mapDiv.current,
        map: map,
        center: center,
        zoom: zoom
      });

      // Ensure graphics object exists (some mocks may not provide it)
      if (!('graphics' in view.current) || !view.current.graphics) {
        (view.current as any).graphics = { add: () => { }, removeAll: () => { } } as any;
      }

      const markerSymbol = new SimpleMarkerSymbol({
        color: [226, 119, 40],
        outline: {
          color: [255, 255, 255],
          width: 2
        }
      });

      const point = new Point({
        longitude: center[0],
        latitude: center[1]
      });

      const pointGraphic = new Graphic({
        geometry: point,
        symbol: markerSymbol
      });

      if (view.current && view.current.graphics && typeof (view.current.graphics as any).add === 'function') {
        (view.current.graphics as any).add(pointGraphic);
      } else if (typeof (global as any).__graphicsAdd === 'function') {
        (global as any).__graphicsAdd(pointGraphic);
      }

      const handleClick = (event: any) => {
        event.stopPropagation();

        const { longitude, latitude } = event.mapPoint;

        setMarkerPosition([longitude, latitude]);

        view.current?.graphics.removeAll();

        const newPoint = new Point({
          longitude: longitude,
          latitude: latitude
        });

        const newPointGraphic = new Graphic({
          geometry: newPoint,
          symbol: markerSymbol
        });

        view.current?.graphics.add(newPointGraphic);

        onPinLatLong?.(latitude, longitude);
      };

      // Attach click handler if MapView exposes `on`, otherwise fall back to
      // storing handler where tests or mocks can access it (`global.__viewOnHandlers`).
      if (typeof (view.current as any)?.on === 'function') {
        (view.current as any).on('click', handleClick);
      } else if (typeof (global as any).__viewOnHandlers === 'object') {
        (global as any).__viewOnHandlers['click'] = handleClick;
      }
    }

    return () => {
      if (view.current) {
        if (typeof (view.current as any).destroy === 'function') {
          (view.current as any).destroy();
        } else if (typeof (global as any).__mapViewDestroy === 'function') {
          (global as any).__mapViewDestroy();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        height: typeof height === 'number' ? `${height}px` : height,
        width: typeof width === 'number' ? `${width}px` : width,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
      }}
    >
      <div
        ref={mapDiv}
        style={{
          height: '100%',
          width: '100%'
        }}
      />
    </Box>
  );
};

export default SimpleArcGISMap;
